/**
 * @owner @Appboy/intelligence
 */

import type { MessageGet, MessagePost, SmsMessageGet, SmsMessagePost } from "@schemas/types";

import type { ContentOptimizationErrorTextKey } from "./types";

export type SmsMessageData = SmsMessageGet | SmsMessagePost;

export const isSmsMessage = (msg: MessageGet | MessagePost): msg is SmsMessageData => {
  return msg.message_type === "sms";
};

/**
 * Extracts text fields from an SMS/RCS message for Content Optimization validation.
 * All text content is collected under the "generic" error text key.
 *
 * For RCS messages, extracts from rcs_message.body (Message/Description),
 * rcs_message.title (Title on rich cards), and rcs_message.media_url
 * (asset URL on media / media rich cards).
 *
 * For plain SMS/MMS messages, extracts from body and any media_items[].url
 * (MMS asset URLs can contain Liquid and must be checked by validators).
 *
 * @param messageData - The SMS/RCS message data (GET or POST shape)
 * @returns An object mapping the "generic" error text key to arrays of text values
 */
export const extractSmsMessageTexts = (
  messageData: SmsMessageData
): Partial<Record<ContentOptimizationErrorTextKey, string[]>> => {
  const texts: string[] = [];

  if (messageData.protocol === "rcs" && messageData.rcs_message) {
    const rcs = messageData.rcs_message;
    if (rcs.body) {
      texts.push(rcs.body);
    }
    if ("title" in rcs && rcs.title) {
      texts.push(rcs.title);
    }
    if ("media_url" in rcs && rcs.media_url) {
      texts.push(rcs.media_url);
    }
  } else {
    if (messageData.body) {
      texts.push(messageData.body);
    }
    messageData.media_items?.forEach((item) => {
      if (item.url) {
        texts.push(item.url);
      }
    });
  }

  if (texts.length === 0) {
    return {};
  }

  return { generic: texts };
};
